import React from 'react';
import { BellFilled, StarFilled } from '@ant-design/icons';
import useMountEffect from '../../hooks/useMountEffect';
import { isWorkOrderRemindersEnabled } from '../../data/workOrderStarLabels';
import { RingingBellBadge } from './RingingBellBadge';
import connectActiveReminderCount, { ActiveReminderCountProps } from './connectActiveReminderCount';

/**
 * The Tracked view's tab icon (Figma 13702-94889): a gold star/bell while none of the user's
 * reminders are due, a ringing red bell once any is. The due count is a server search
 * (refreshActiveReminderCount), re-read on mount and after every star write; a reminder firing over
 * the WebSocket adds one (ReminderAlertStack).
 */
const TrackedViewTabIcon: React.FC<ActiveReminderCountProps> = ({
	activeReminderCount,
	refreshCount,
}) => {
	useMountEffect(() => {
		refreshCount();
	});

	// A block-level flex box exactly 22px tall, like the text tabs' line box (see renderTabName).
	// inline-flex would sit on the text baseline and add descender space below it, making the
	// whole tab row taller than its siblings — the selected tab then stops short of the bottom edge.
	// Reminders off (Work-Order-Reminders): just the star — no bell, no due count.
	if (!isWorkOrderRemindersEnabled()) {
		return (
			<span className="flex h-[22px] items-center justify-center">
				<StarFilled className="text-lg !text-calendulaGold-5" />
			</span>
		);
	}
	if (activeReminderCount > 0) {
		return (
			<span
				title={`${activeReminderCount} reminder${activeReminderCount === 1 ? '' : 's'} due`}
				className="flex h-[22px] items-center justify-center"
			>
				<RingingBellBadge />
			</span>
		);
	}
	return (
		<span className="flex h-[22px] items-center justify-center gap-0.5">
			<StarFilled className="text-lg !text-calendulaGold-5" />
			{/* Takes the tab's text color: white on an unselected tab, dark on the selected one. */}
			<span className="bodyregular">/</span>
			<BellFilled className="text-lg !text-calendulaGold-5" />
		</span>
	);
};

export default connectActiveReminderCount(TrackedViewTabIcon);
